// src/pages/TrackRecord.jsx
// Public trade data analytics page — no $ amounts shown, pulls live from Supabase
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { calcStats, getEquityCurveData } from '../utils/stats';
import ODFLogo from '../components/ODFLogo';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid
} from 'recharts';
import { Link2, Check } from 'lucide-react';

const ODF_USER_ID = 'ff2986f5-583c-4164-9980-08dc47b5a621';
const G = '#00e5a0';

function StatCard({ label, value, color = '#f0f0f5', sub }) {
  return (
    <div style={{
      background: '#16161f', border: '1px solid #1e1e2e',
      borderRadius: 12, padding: '20px 22px',
    }}>
      <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, fontFamily: "'DM Mono', monospace", letterSpacing: '-0.02em' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#55556a', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#16161f', border: '1px solid #2a2a3e', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ color: '#55556a', fontFamily: "'DM Mono', monospace", fontSize: 10, marginBottom: 4 }}>{label}</div>
        <div style={{ color: G, fontFamily: "'DM Mono', monospace" }}>
          {payload[0].value > 0 ? '+' : ''}{payload[0].value.toFixed(1)}%
        </div>
      </div>
    );
  }
  return null;
};

export default function TrackRecord({ onBack, onSignIn }) {
  const [trades,   setTrades]   = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    async function load() {
      const [{ data: tradesData }, { data: accountsData }] = await Promise.all([
        supabase.from('trades').select('*').eq('user_id', ODF_USER_ID).order('date'),
        supabase.from('accounts').select('*').eq('user_id', ODF_USER_ID),
      ]);
      setTrades(tradesData || []);
      setAccounts(accountsData || []);
      setLoading(false);
    }
    load();
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Map DB trades to app format
  const mapTrade = t => ({
    id: t.id, date: t.date, pair: t.pair,
    pnl: t.pnl, result: t.result,
    rrAchieved: t.rr_achieved,
    followedRules: t.followed_rules,
    mistakeMade: t.mistake_made,
    session: t.session,
  });

  const filteredTrades = activeTab === 'all'
    ? trades.map(mapTrade)
    : trades.filter(t => t.account_id === activeTab).map(mapTrade);

  // Real starting balance for the current tab:
  //   - "All accounts" → sum across all accounts
  //   - Specific account → that account's starting_balance
  // Falls back to $10K only if no account data has loaded.
  const startBal = (() => {
    if (activeTab === 'all') {
      const total = accounts.reduce((sum, a) => sum + Number(a.starting_balance || 0), 0);
      return total > 0 ? total : 10000;
    }
    const acc = accounts.find(a => a.id === activeTab);
    const bal = Number(acc?.starting_balance);
    return bal > 0 ? bal : 10000;
  })();

  const s = calcStats(filteredTrades, { startingBalance: startBal });

  // Equity curve as % growth — uses REAL starting balance
  const equityData = (() => {
    if (!filteredTrades.length) return [];
    const sorted = [...filteredTrades].sort((a, b) => new Date(a.date) - new Date(b.date));
    let pct = 0;
    return sorted.map(t => {
      const tradePct = (t.pnl && startBal > 0) ? (Number(t.pnl) / startBal) * 100 : 0;
      pct += tradePct;
      return { date: t.date, growth: Number(pct.toFixed(2)) };
    });
  })();

  // Monthly % returns — also against real starting balance
  const monthlyReturns = (() => {
    const byMonth = {};
    for (const t of filteredTrades) {
      const m = t.date?.slice(0, 7);
      if (!m) continue;
      if (!byMonth[m]) byMonth[m] = 0;
      byMonth[m] += Number(t.pnl || 0);
    }
    return Object.entries(byMonth)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, pnl]) => ({
        month,
        pct: Number((startBal > 0 ? (pnl / startBal) * 100 : 0).toFixed(1))
      }));
  })();

  // Best pairs
  const pairStats = (() => {
    const p = {};
    for (const t of filteredTrades) {
      if (!t.pair) continue;
      if (!p[t.pair]) p[t.pair] = { wins: 0, total: 0 };
      p[t.pair].total++;
      if (t.result === 'Win') p[t.pair].wins++;
    }
    return Object.entries(p)
      .map(([pair, d]) => ({ pair, wr: Math.round(d.wins / d.total * 100), total: d.total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  })();

  // Win streak
  const streaks = (() => {
    const sorted = [...filteredTrades].sort((a, b) => new Date(a.date) - new Date(b.date));
    let cur = 0, best = 0;
    for (const t of sorted) {
      if (t.result === 'Win') { cur++; best = Math.max(best, cur); }
      else cur = 0;
    }
    return { current: cur, best };
  })();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: G, fontFamily: "'DM Mono', monospace", fontSize: 13 }}>Loading trade data analytics...</div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', color: '#f0f0f5', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #1e1e2e',
        padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ cursor: 'pointer' }} onClick={onBack}>
          <ODFLogo size="compact" />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={copyLink} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: '1px solid #1e1e2e',
            borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
            color: '#8888a8', fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif",
            transition: 'all 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.color = G; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e2e'; e.currentTarget.style.color = '#8888a8'; }}>
            {copied ? <><Check size={13} /> Copied</> : <><Link2 size={13} /> Share</>}
          </button>
          <button onClick={onSignIn} style={{
            background: G, color: '#0a0a0f', border: 'none',
            borderRadius: 8, padding: '8px 20px', fontSize: 13,
            fontWeight: 700, cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif",
          }}>
            Sign In →
          </button>
        </div>
      </nav>

      <div style={{ maxWidth: 1060, margin: '0 auto', padding: '48px 24px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(0,229,160,0.1)', border: '1px solid rgba(0,229,160,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            fontFamily: "'Georgia', serif", fontSize: 20, color: G,
          }}>ODF</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 8px' }}>
            ODF's Live Dashboard
          </h1>
          <p style={{ fontSize: 14, color: '#8888a8', margin: 0 }}>
            Live trading data · Manually logged · Updated every session
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.2)', borderRadius: 100, padding: '4px 14px', fontSize: 11, color: G, fontFamily: "'DM Mono', monospace" }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: G, display: 'inline-block' }} />
            LIVE · {filteredTrades.length} TRADES TRACKED
          </div>
        </div>

        {/* Account tabs */}
        {accounts.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => setActiveTab('all')} style={{
              padding: '7px 18px', borderRadius: 8, border: '1px solid',
              fontSize: 12, cursor: 'pointer', outline: 'none',
              fontFamily: "'IBM Plex Sans', sans-serif",
              background: activeTab === 'all' ? 'rgba(0,229,160,0.1)' : 'transparent',
              borderColor: activeTab === 'all' ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
              color: activeTab === 'all' ? G : '#8888a8',
            }}>All Accounts</button>
            {accounts.map(acc => (
              <button key={acc.id} onClick={() => setActiveTab(acc.id)} style={{
                padding: '7px 18px', borderRadius: 8, border: '1px solid',
                fontSize: 12, cursor: 'pointer', outline: 'none',
                fontFamily: "'IBM Plex Sans', sans-serif",
                background: activeTab === acc.id ? 'rgba(0,229,160,0.1)' : 'transparent',
                borderColor: activeTab === acc.id ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
                color: activeTab === acc.id ? G : '#8888a8',
              }}>{acc.name}</button>
            ))}
          </div>
        )}

        {/* Key stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          <StatCard label="Win Rate"       value={`${s.winRate}%`}         color={G}        sub={`${s.wins}W · ${s.losses}L · ${s.breakevens}BE`} />
          <StatCard label="ROI"           value={`${s.roi >= 0 ? '+' : ''}${s.roi}%`}  color={s.roi >= 0 ? G : '#ff4d6d'} sub="Return on capital" />
          <StatCard label="Average RR"     value={`${s.avgRR}R`}           color={G}        sub="Per trade" />
          <StatCard label="Profit Factor"  value={s.profitFactor}          color={parseFloat(s.profitFactor) >= 1.5 ? G : '#ffd166'} sub="Gross P / Gross L" />
          <StatCard label="Discipline"     value={`${s.ruleFollowPct}%`}   color={s.ruleFollowPct >= 80 ? G : '#ffd166'} sub="Rule adherence" />
          <StatCard label="Total Trades"   value={s.totalTrades}           sub="All time" />
          <StatCard label="Best Win Streak" value={`${streaks.best}`}      color={G}        sub={`Current: ${streaks.current}`} />
        </div>

        {/* Equity curve — % based */}
        <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 14, padding: '22px 24px', marginBottom: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f0f5' }}>Performance Curve</div>
            <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
              Cumulative growth · % based
            </div>
          </div>
          {equityData.length > 1 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={equityData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="trGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={G} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={G} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" />
                <XAxis dataKey="date" tick={{ fill: '#55556a', fontSize: 9, fontFamily: "'DM Mono', monospace" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#55556a', fontSize: 9, fontFamily: "'DM Mono', monospace" }} tickLine={false} axisLine={false} tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="growth" name="Growth" stroke={G} strokeWidth={2} fill="url(#trGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#55556a', fontSize: 13 }}>
              Not enough data yet
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {/* Monthly returns */}
          <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 14, padding: '22px 24px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5', marginBottom: 16 }}>Monthly Performance</div>
            {monthlyReturns.length === 0 ? (
              <div style={{ color: '#55556a', fontSize: 13 }}>No data yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {monthlyReturns.slice(-6).map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", minWidth: 55 }}>{m.month}</span>
                    <div style={{ flex: 1, background: '#1e1e2e', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, Math.abs(m.pct) * 5)}%`,
                        height: '100%',
                        background: m.pct >= 0 ? G : '#ff4d6d',
                        borderRadius: 4
                      }} />
                    </div>
                    <span style={{
                      fontSize: 12, fontFamily: "'DM Mono', monospace", fontWeight: 600, minWidth: 40, textAlign: 'right',
                      color: m.pct >= 0 ? G : '#ff4d6d'
                    }}>
                      {m.pct >= 0 ? '+' : ''}{m.pct}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Best pairs */}
          <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 14, padding: '22px 24px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5', marginBottom: 16 }}>Performance by Pair</div>
            {pairStats.length === 0 ? (
              <div style={{ color: '#55556a', fontSize: 13 }}>No data yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pairStats.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12, color: '#f0f0f5', fontWeight: 500, minWidth: 70 }}>{p.pair}</span>
                    <div style={{ flex: 1, background: '#1e1e2e', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                      <div style={{ width: `${p.wr}%`, height: '100%', background: p.wr >= 60 ? G : p.wr >= 40 ? '#ffd166' : '#ff4d6d', borderRadius: 4 }} />
                    </div>
                    <span style={{
                      fontSize: 12, fontFamily: "'DM Mono', monospace", fontWeight: 600, minWidth: 36, textAlign: 'right',
                      color: p.wr >= 60 ? G : p.wr >= 40 ? '#ffd166' : '#ff4d6d'
                    }}>
                      {p.wr}%
                    </span>
                    <span style={{ fontSize: 10, color: '#55556a', minWidth: 20 }}>{p.total}t</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{ textAlign: 'center', fontSize: 11, color: '#2a2a3e', fontFamily: "'DM Mono', monospace", marginTop: 32 }}>
          Past performance does not guarantee future results · Trading involves risk
        </div>
      </div>
    </div>
  );
}
